/**
 * Last-resort UI for when the client app cannot start.
 *
 * `render()` in src/index.js cannot mount the app until the hosted assets have
 * been fetched, because translations and the hosted config come from them. That
 * fetch is keyed by the Sharetribe client ID:
 *
 *   https://cdn.st-api.com/v1/assets/pub/<REACT_APP_SHARETRIBE_SDK_CLIENT_ID>/...
 *
 * so a wrong or placeholder client ID, an unreachable CDN, or an inactive
 * marketplace all reject it. That rejection used to be logged and nothing else,
 * leaving `#root` empty — a white page with no text. Sellers reported it as
 * "I can't log in", because the login form is one of the things that never
 * appears, and the page offered them no way to tell a marketplace outage from a
 * rejected password.
 *
 * This is deliberately plain DOM with inline styles. Everything richer —
 * React components, the stylesheet, translated strings — depends on the
 * configuration that just failed to load, so reaching for it here risks
 * throwing a second time and restoring the blank page.
 */

/**
 * True when the container holds nothing a visitor could read or use.
 *
 * On the hydration path the server may already have written usable markup. A
 * failed client start is bad, but replacing served content with an error notice
 * is worse, so in that case the existing markup is left alone.
 *
 * @param {Element} container
 * @returns {boolean}
 */
export const isEffectivelyEmpty = container => {
  if (!container) {
    return false;
  }
  if (container.children.length > 0) {
    return false;
  }
  return (container.textContent || '').trim() === '';
};

/**
 * Keep this failure out of search results, and give the tab a name.
 *
 * Two separate problems, both only visible on this path:
 *
 * `public/index.html` carries no `<title>` of its own — just the `<!--!title-->`
 * placeholder that src/components/Page/Page.js fills through Helmet when the app
 * renders. The app did not render, so the tab, the history entry and any
 * bookmark are all untitled.
 *
 * Worse for search: this failure is served with whatever status the original
 * request got, normally HTTP 200, on whatever URL was asked for. A crawler
 * fetching a listing or an article during an asset outage gets 200 plus "we
 * can't load the marketplace" — a soft 404, which can cost the real page its
 * place in the index. `noindex` makes a transient outage unable to do that.
 *
 * Deliberately no description, Open Graph or keyword tags. A noindex page has no
 * use for them, and dressing up an error page with marketing copy is how a
 * marketplace ends up ranking for its own outage.
 *
 * `public/404.html` and `public/500.html` need none of this: server/index.js
 * serves them with real 404 and 500 statuses, which already keeps them out of
 * the index.
 *
 * @param {Document} doc
 */
const applyFailurePageMetadata = doc => {
  // Only name the page if nothing already did. On a hydration path the server
  // may have set a real title, and it describes the page better than this does.
  if (!(doc.title || '').trim()) {
    doc.title = 'Temporarily unavailable';
  }

  const head = doc.head || doc.getElementsByTagName('head')[0];
  if (!head) {
    return;
  }

  // Update rather than append: a second, disagreeing robots tag is worse than
  // none, and crawlers honour the most restrictive one they find.
  let robots = head.querySelector('meta[name="robots"]');
  if (!robots) {
    robots = doc.createElement('meta');
    robots.setAttribute('name', 'robots');
    head.appendChild(robots);
  }
  robots.setAttribute('content', 'noindex');
};

/**
 * State the HTTP status the configuration request came back with.
 *
 * This is the one fact that separates the possible causes, and until it is on
 * the page the only way to read it is to open devtools — which is not something
 * you can ask a seller to do, and not something the people who can fix the
 * cause were doing either. `hostedAssets.duck.js` already captures it: the
 * thunk is unwrapped at line 134, so the rejection that reaches
 * src/index.js is the `storableError` object, carrying `status`.
 *
 * A status and a status text are not sensitive. The client ID is deliberately
 * not shown — it is in the bundle anyway, but a page is not the place for it.
 *
 * @param {object} [error] storableError-shaped rejection
 * @returns {string|null} a line to show, or null when there is nothing to say
 */
const describeFailure = error => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const { status, statusText } = error;

  if (typeof status === 'number' && status > 0) {
    const suffix = statusText ? ` ${statusText}` : '';
    return `Configuration request failed with HTTP ${status}${suffix}.`;
  }

  // No status means the request never got a response at all — DNS, TLS, a
  // blocked host or an offline client. Saying "no response" is the honest
  // description; claiming a status we do not have would not be.
  return 'Configuration request failed with no HTTP response.';
};

/**
 * Replace an empty #root with a message that names the real situation.
 *
 * Note what it does not say: nothing about the visitor's email or password. The
 * app has no evidence either is wrong at this point — it never got far enough
 * to ask — and guessing was the original defect.
 *
 * @param {Document} doc document to render into
 * @param {object} [error] the rejection, so the page can report its status
 * @returns {boolean} whether the fallback was rendered
 */
export const renderStartupFailure = (
  doc = typeof document !== 'undefined' ? document : null,
  error = null
) => {
  if (!doc) {
    return false;
  }

  const container = doc.getElementById('root');
  if (!isEffectivelyEmpty(container)) {
    return false;
  }

  applyFailurePageMetadata(doc);

  const wrap = doc.createElement('div');
  wrap.setAttribute('role', 'alert');
  wrap.setAttribute('data-testid', 'startup-failure');
  wrap.style.cssText =
    'font-family:Inter,system-ui,sans-serif;max-width:32rem;margin:0 auto;' +
    'padding:3rem 1rem;color:#0a2540;line-height:1.6;';

  const heading = doc.createElement('h1');
  heading.textContent = "We can't load the marketplace right now";
  heading.style.cssText = 'font-size:1.375rem;margin:0 0 0.75rem;';

  const body = doc.createElement('p');
  body.textContent =
    'This is a problem on our side, not with your account — your email and ' +
    'password are fine. The marketplace could not load its configuration, so ' +
    'nothing on the page could start.';
  body.style.cssText = 'margin:0 0 1.25rem;';

  const retry = doc.createElement('button');
  retry.type = 'button';
  retry.textContent = 'Try again';
  retry.style.cssText =
    'background:#0a2540;color:#fff;border:none;border-radius:8px;' +
    'padding:0.75rem 1.5rem;font-size:0.9375rem;font-weight:600;cursor:pointer;';
  retry.addEventListener('click', () => {
    if (doc.defaultView && doc.defaultView.location) {
      doc.defaultView.location.reload();
    }
  });

  wrap.appendChild(heading);
  wrap.appendChild(body);
  wrap.appendChild(retry);

  const diagnostic = describeFailure(error);
  if (diagnostic) {
    const note = doc.createElement('p');
    note.setAttribute('data-testid', 'startup-failure-diagnostic');
    note.textContent = `${diagnostic} Quote this when reporting the problem.`;
    note.style.cssText =
      'margin:1.5rem 0 0;font-size:0.8125rem;color:#6b7c93;' +
      'font-family:ui-monospace,SFMono-Regular,Menlo,monospace;';
    wrap.appendChild(note);
  }

  container.appendChild(wrap);

  return true;
};
